import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { take } from 'rxjs/operators';
import {
  SageBatchDetail,
  SageBatchDocument,
  SageBatchCsv,
  SageConfirmResult
} from '../../models/sage-export.model';
import { SageExportService } from '../../services/sage-export.service';

@Component({
    selector: 'app-sage-export-confirm',
    templateUrl: './sage-export-confirm.component.html',
    styleUrls: ['./sage-export-confirm.component.scss'],
    standalone: false
})
export class SageExportConfirmComponent implements OnInit {

  batchRef: string = '';

  batch: SageBatchDetail = null;

  loading: boolean = true;
  saving: boolean = false;

  /** Keyed by attemptId. A document is in here when the operator says Sage took it. */
  accepted: { [attemptId: number]: boolean } = {};

  outcome: string = '';

  /** The backend slices this at 2000, so the limit is shown rather than discovered. */
  outcomeLimit: number = 2000;

  isError: boolean = false;
  errorMessage: string = '';

  /** Set after a successful confirm, from the re-read detail rather than the returned counts. */
  summary: string = '';

  constructor(private route: ActivatedRoute,
    private sageExportService: SageExportService) { }

  ngOnInit(): void {
    this.batchRef = this.route.snapshot.paramMap.get('batchRef');

    this.load();
  }

  load() {
    this.loading = true;

    this.sageExportService.getBatch(this.batchRef)
    .pipe(take(1))
    .subscribe({
      next: (data: SageBatchDetail) => {
        this.batch = data;
        this.loading = false;
        this.resetTicks();
      },
      error: (e: any) => {
        this.loading = false;

        if (e.status === 401 || e.status === 403) {
          return;
        }

        this.isError = true;
        this.errorMessage = this.firstFault(e, 'Could not load this batch. Check the reference.');
      }
    })
  }

  /**
   * Ticks the documents already confirmed, and only those.
   *
   * An already-confirmed document is always submitted as accepted. The backend leaves such a row
   * untouched, but it REWRITES `outcome` on every row it is not told was accepted - so omitting
   * one would overwrite the note recorded when it actually went into Sage with whatever is typed
   * today.
   */
  private resetTicks() {
    this.accepted = {};

    this.documents.forEach((doc: SageBatchDocument) => {
      if (doc.confirmed) {
        this.accepted[doc.attemptId] = true;
      }
    })
  }

  get documents(): SageBatchDocument[] {
    return this.batch && this.batch.documents ? this.batch.documents : [];
  }

  isTicked(doc: SageBatchDocument): boolean {
    return !!this.accepted[doc.attemptId];
  }

  onTick(doc: SageBatchDocument, ticked: boolean) {
    // A confirmed document cannot be untangled from here: the backend never clears `confirmedAt`,
    // so an unticked box would be a promise the screen cannot keep.
    if (doc.confirmed) {
      return;
    }

    this.accepted[doc.attemptId] = ticked;
  }

  tickAll() {
    this.documents.forEach((doc: SageBatchDocument) => {
      this.accepted[doc.attemptId] = true;
    })
  }

  /** Leaves the already-confirmed ones alone, for the reason in `resetTicks`. */
  untickAll() {
    this.documents.forEach((doc: SageBatchDocument) => {
      this.accepted[doc.attemptId] = !!doc.confirmed;
    })
  }

  /** Documents that will be marked as in Sage, including the ones already confirmed. */
  get acceptedCount(): number {
    return this.documents.filter((doc: SageBatchDocument) => this.isTicked(doc)).length;
  }

  /** Documents that stay outstanding and return in the next batch. */
  get outstandingCount(): number {
    return this.documents.length - this.acceptedCount;
  }

  /** Newly ticked this visit - what the operator is actually about to record. */
  get newlyAcceptedCount(): number {
    return this.documents
      .filter((doc: SageBatchDocument) => this.isTicked(doc) && !doc.confirmed).length;
  }

  get alreadyConfirmedCount(): number {
    return this.documents.filter((doc: SageBatchDocument) => doc.confirmed).length;
  }

  /**
   * True once any part of this batch is in Sage.
   *
   * The batch file still contains the accepted documents, so re-importing it would post them a
   * second time. The screen says so rather than relying on the operator remembering.
   */
  get isPartlyInSage(): boolean {
    return this.alreadyConfirmedCount > 0;
  }

  onSubmit() {
    if (!this.batch) {
      return;
    }

    if (!window.confirm(this.confirmText())) {
      return;
    }

    this.isError = false;
    this.errorMessage = '';
    this.summary = '';
    this.saving = true;

    this.sageExportService.confirm(this.batchRef,
      this.acceptedIds('SI'),
      this.acceptedIds('SC'),
      this.outcome)
    .pipe(take(1))
    .subscribe({
      next: (data: SageConfirmResult) => {
        this.saving = false;
        this.summary = data.confirmed + ' newly recorded as in Sage, '
          + data.alreadyConfirmed + ' already were, '
          + data.stillWaiting + ' still outstanding and returning in the next batch.';

        // Re-read rather than paint from the counts: the row states, and what is now tickable,
        // come from the batch itself.
        this.load();
      },
      error: (e: any) => {
        this.saving = false;

        if (e.status === 401 || e.status === 403) {
          return;
        }

        this.isError = true;
        this.errorMessage = this.firstFault(e, 'Could not record the confirmation. Please try again.');
      }
    })
  }

  /**
   * An empty confirmation is legitimate - Sage can reject an entire file - but it is also what a
   * mis-click produces, so it gets its own wording rather than a count of zero.
   */
  private confirmText(): string {
    if (this.newlyAcceptedCount === 0 && this.alreadyConfirmedCount === 0) {
      return 'Sage accepted none of these? All ' + this.documents.length
        + ' documents stay outstanding and return in the next batch.';
    }

    return this.acceptedCount + ' document(s) will be marked as in Sage. '
      + this.outstandingCount + ' will stay outstanding and return in the next batch. Continue?';
  }

  private acceptedIds(docType: string): number[] {
    return this.documents
      .filter((doc: SageBatchDocument) => doc.docType === docType && this.isTicked(doc))
      .map((doc: SageBatchDocument) => doc.docId);
  }

  downloadAgain() {
    if (!this.batch || !this.batch.csvAvailable) {
      return;
    }

    this.sageExportService.getBatchCsv(this.batchRef)
    .pipe(take(1))
    .subscribe({
      next: (data: SageBatchCsv) => {
        this.sageExportService.downloadCsv(data.batchRef, data.csv);
      },
      error: (e: any) => {
        if (e.status === 401 || e.status === 403) {
          return;
        }

        this.isError = true;
        this.errorMessage = this.firstFault(e, 'The file for this batch could not be fetched.');
      }
    })
  }

  private firstFault(e: any, fallback: string): string {
    const faults: string[] = this.sageExportService.faultsFrom(e);

    return faults.length > 0 ? faults.join(' ') : fallback;
  }

  /**
   * Money, formatted here rather than through any shared quantity helper. Quantities and money
   * are different things with different precision, and one primitive doing both is how they end
   * up rounded alike.
   */
  formatMoney(value: number): string {
    if (value === null || value === undefined) {
      return '—';
    }

    const amount: number = Number(value);

    return (amount < 0 ? '-£' : '£') + Math.abs(amount).toFixed(2);
  }

  formatDate(value: string): string {
    return value ? moment(value).format('DD/MM/YYYY') : '—';
  }

  formatDateTime(value: string): string {
    return value ? moment(value).format('DD/MM/YYYY HH:mm') : '—';
  }

  docTypeLabel(doc: SageBatchDocument): string {
    return doc.docType === 'SC' ? 'Credit note' : 'Invoice';
  }

  /** A document that has moved or vanished since the file was built is called out on its row. */
  hasWarning(doc: SageBatchDocument): boolean {
    return doc.changedSinceExport || doc.missing || doc.deleted;
  }
}
